import React, { useState, createContext, useContext, useEffect } from 'react';
import { css } from '@emotion/css';
import PropTypes from 'prop-types';
import { get, groupBy, map, sortBy, take, isEmpty, invoke, toNumber, includes, keys, mapValues, pick } from 'lodash';
import moment from 'moment';
import Checkbox from '../ScheduledItems/Checkbox';
import AppLabels from '../../utils/appLabels';
import Loader from '../../common/Loading/loader';
import UserMessage from '../../common/UserMessage/UserMessage';
import { PreferencesContext, PreferencesContextType } from '../Preferences/PreferencesContext';
import { ModalContext, ModalContextType } from '../../common/Modal/Modal';
import { Spinner } from '../../common/Spinner/Spinner';
import { AvailableSlotsPayload } from '../../service/get-all-available-slots';
import { formatPrice } from '../../utils/formatString';
import { SessionContext, SessionContextType } from '../LandingPage/SessioncContext';

const SlotPreferencesStyle = css`
  width: 100%;
`;

const SlotColumnsContainerStyle = css`
  display: flex;
  width: 100%;
  gap: 1.5rem;
  min-width: 51rem; /* 3 14rem cols + 1rem padding left and right + 1.5rem gap */
  min-height: 21rem;
  max-height: 21rem;
  overflow: hidden auto;

  @media (max-width: 641px) {
    flex-direction: column;
    overflow-y: auto;
    gap: 0.5rem;
    -ms-overflow-style: none;
    scrollbar-width: none;
    min-width: initial;
    min-height: initial;
    max-height: initial;
  }
`;

const timeSlotHeadersRow = css`
  display: flex;
  gap: 0.5rem;

  @media (max-width: 641px) {
    max-width: 90vw;
    overflow: auto hidden;
  }

  & > .slot_header {
    padding: 0.75rem 1.25rem;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    flex: 1;

    border-radius: 0.5rem;
    border: 1px solid #7d7b78;
    background: #fff;

    cursor: pointer;
    color: #2a2928;
  }

  & > .slot_header.active {
    background: #7b1a27;
    border: 0;
    color: #fff;
  }

  p {
    margin: 0;
  }
`;

const SlotColumnStyle = css`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 0.5rem 1rem;
  min-width: 14rem;

  @media (max-width: 641px) {
    min-width: 50vw;
  }
`;

const SlotColumnTitleStyle = css`
  padding: 0.75rem 0;
  font-size: 1.125rem;
  font-weight: 500;
  line-height: 125%;
  overflow: hidden hidden;
  -ms-overflow-style: none;
  scrollbar-width: none;
  min-height: 3rem;
  box-sizing: border-box;
`;

const MoreOptionsButtonStyle = css`
  display: flex;
  align-items: center;
  border: 0;
  background: 0;
  padding: 0.5rem 0;
  cursor: pointer;
  color: #2a2928;
`;

const SlotRowStyle = css`
  display: flex;
  padding: 1rem;
  color: #2a2928;
  border-bottom: 1px solid #edeae9;
`;

const SlotTimeStyle = css`
  flex: 1;
  font-size: 1rem;
  line-height: 1.375rem;
  font-weight: 400;
`;

const slotPriceStyle = css`
  color: #7b1a27;
`;

const buttonContainer = css`
  display: flex;
  justify-content: flex-end;
  padding-top: 1rem;

  & p {
    margin: 0;
    flex: 1;
    padding: 1rem;
    font-size: 0.875rem;
    color: #2a2928;
  }

  button[disabled] {
    background: #f1f0ee;
    color: #535250;
  }

  button.saving[disabled] {
    background-color: #7b1a27;
    color: #fff;
  }

  @media (max-width: 425px) {
    flex-direction: column;
    align-items: center;
  }
`;

const saveSlotsButtonStyle = css`
  background-color: #7b1a27;
  color: #fff;
  border: 0;
  border-radius: 6.25rem;
  padding: 0.5rem 0.75rem;
  min-width: 14rem;
  cursor: pointer;
  min-height: 3.3rem;

  @media (max-width: 425px) {
    min-width: 17.4rem;
  }
`;

const errorContainer = css`
  width: 100%;
  padding: 1rem;
`;

const reservedStyle = css`
  color: #000;
`;

const noReservedStyle = css`
  padding: 0 1rem;
  color: #7b1a27;
`;

const reservationTitleStyle = css`
  margin: 0 0 0.5rem;
`;

const titleStyle = css`
  margin: 1rem 0 0.5rem;
`;

const freePriceStyle = css`
  color: #437f2f;
  font-weight: 600;
`;

type TimeSlot = {
  id: string;
  dayOfWeek: 'SUNDAY' | 'MONDAY' | 'TUESDAY' | 'WEDNESDAY' | 'THURSDAY' | 'FRIDAY' | 'SATURDAY';
  serviceType: string;
  slotStartTime: number;
  slotEndTime: number;
  slotExpiryTime: number;
  charge: {
    deliveryCharge: number;
    minimumBasketSize: number;
    reducedDeliveryCharge: number;
  };
  status: string;
  displaySlotType: string;
  slotdisplayStart: number;
  dropTime: number;
  tillByTime: number;
  stageByTime: number;
  priceType: string[];
  pickerOption: string;
  fullFillmentVendor: string;
};

const SlotDayHeader = (props) => {
  const day = get(props, 'day');
  const active: boolean = get(props, 'active');
  const handleClick = get(props, 'onClick');

  const date = moment(day, 'YYYY-MM-DD');

  return (
    <button className={`slot_header ${active ? ' active' : ''}`} onClick={handleClick}>
      <p className={'body-text'}>{date.format('ddd')}</p>
      <p className={'body-text-sm'}>{date.format('MMM, D')}</p>
    </button>
  );
};

SlotDayHeader.propTypes = {
  day: PropTypes.string.isRequired,
  active: PropTypes.bool,
  onClick: PropTypes.func,
};

const analyticsEvents = (modalAction, selectedSlot) => {
  try {
    if (window['eddlLoadComplete']) {
      const ARObject = {
        dayOfWeek: selectedSlot?.dayOfWeek,
        slotStartTime: selectedSlot?.displayStartTS,
        slotEndTime: selectedSlot?.displayEndTS,
        slotType: selectedSlot?.slotType,
      };
      const autoReplenishmentObj = window['AB']?.DATALAYER?.setDeliveryWindowAR(ARObject, 'newSNSPage');
      window['AB']?.DATALAYER?.setModalClickEventsAR(modalAction, autoReplenishmentObj);
    }
  } catch (error) {
    // Handle any errors that occur during execution
    console.error(`@Analytics Team -> setModalClickEventsAR or setDeliveryWindowAR functions: ${error}`);
  }
};

const SlotTimesColumn = (props) => {
  const [expanded, setExpanded] = useState(false);
  const columnData: TimeSlot[] = sortBy(get(props, 'columnData'), 'slotStartTime');
  const { selectedSlot, setSelectedSlot } = useContext(TimeSlotContext) as TimeSlotContext;

  return (
    <div className={SlotColumnStyle}>
      <div>
        {map(expanded ? columnData : take(columnData, 5), (slot: TimeSlot) => {
          const slotStartTime: string = moment(get(slot, 'displayStartTS'), 'YYYY-MM-DDThh:mm:ss').format('h A');
          const slotEndTime: string = moment(get(slot, 'displayEndTS'), 'YYYY-MM-DDThh:mm:ss').format('h A');
          const deliveryFeeWaived = get(slot, 'deliveryFeeWaived');

          return (
            <div className={`${SlotRowStyle} body-text-sm`} key={get(slot, 'masterSlotId')}>
              <Checkbox
                className={SlotTimeStyle}
                checked={get(slot, 'masterSlotId') === get(selectedSlot, 'masterSlotId')}
                onChange={() => setSelectedSlot(slot)}
                checkType="circle"
              >
                {`${slotStartTime} - ${slotEndTime}`}
              </Checkbox>
              {deliveryFeeWaived ? (
                <span className={freePriceStyle}>{AppLabels.AvailableSlotPreferences.FREE_PRICE}</span>
              ) : (
                <span className={slotPriceStyle}>{`$${formatPrice(get(slot, 'deliveryCharge', 0))}`}</span>
              )}
            </div>
          );
        })}
      </div>
      {!isEmpty(columnData) ? (
        !expanded ? (
          <div>
            <button
              className={`${MoreOptionsButtonStyle} body-text`}
              onClick={() => setExpanded(true)}
              aria-label="More Options"
            >
              {AppLabels.AvailableSlotPreferences.MORE_OPTIONS}
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M18.5303 9.46967C18.2374 9.17678 17.7626 9.17678 17.4697 9.46967L12 14.9393L6.53033 9.46967C6.23744 9.17678 5.76256 9.17678 5.46967 9.46967C5.17678 9.76256 5.17678 10.2374 5.46967 10.5303L11.4697 16.5303C11.7626 16.8232 12.2374 16.8232 12.5303 16.5303L18.5303 10.5303C18.8232 10.2374 18.8232 9.76256 18.5303 9.46967Z"
                  fill="#2A2928"
                />
              </svg>
            </button>
          </div>
        ) : (
          <div>
            <button
              className={`${MoreOptionsButtonStyle} body-text`}
              onClick={() => setExpanded(false)}
              aria-label="Less Options"
            >
              {AppLabels.AvailableSlotPreferences.LESS_OPTIONS}
              <svg viewBox="0 0 24 24" width="24" height="24">
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M 18.53 16.53 C 18.237 16.823 17.763 16.823 17.47 16.53 L 12 11.061 L 6.53 16.53 C 6.237 16.823 5.763 16.823 5.47 16.53 C 5.177 16.237 5.177 15.763 5.47 15.47 L 11.47 9.47 C 11.763 9.177 12.237 9.177 12.53 9.47 L 18.53 15.47 C 18.823 15.763 18.823 16.237 18.53 16.53 Z"
                  fill="#2A2928"
                ></path>
              </svg>
            </button>
          </div>
        )
      ) : null}
      {isEmpty(columnData) ? (
        <span className="body-text-sm">{AppLabels.AvailableSlotPreferences.EMPTY_COLUMN}</span>
      ) : null}
    </div>
  );
};

SlotTimesColumn.propTypes = {
  columnData: PropTypes.any.isRequired,
};

type slot = {
  id?: string;
};

type TimeSlotContext = {
  selectedSlot: slot;
  setSelectedSlot: (s: slot) => void;
};
export const TimeSlotContext = createContext<TimeSlotContext | null>(null);

const AvailableSlotPreferences = (props) => {
  const currentSessionContext = useContext(SessionContext) as SessionContextType;
  const user = get(currentSessionContext, 'user');
  const currentPreferencesContext = useContext(PreferencesContext) as PreferencesContextType;
  const currentModalContext = useContext(ModalContext) as ModalContextType;

  const [data, setData] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(false);
  const [savingSlots, setSavingSlots] = useState(false);
  const [infoError, setInfoError] = useState(false);
  const nextOrderData: object = get(props, 'nextOrderData', null);

  const slotTypes = ['ONEHR', 'TWOHR', 'FOURHR'];

  const [selectedDay, setSelectedDay] = useState('');
  const [selectedSlot, setSelectedSlot] = useState({});
  const [reservedSlot, setReservedSlot] = useState(null);
  const [isAlternateSlotReserved, setIsAlternateSlotReserved] = useState(false); //TODO update when we know what this is

  const [invalidSelection, setInvalidSelection] = useState(false);
  const [dayColumns, setDayColumns] = useState<string[]>([]);

  const fetchAvailableSlotsInfo = async () => {
    setLoadingInfo(true);

    try {
      const payload: AvailableSlotsPayload = {
        uuid: get(user, 'UUID', ''),
        orderId: get(nextOrderData, 'upcomingOrder.snsNextOrderId', 0),
      };

      const resp = await invoke(currentPreferencesContext, 'fetchAvailableSlotsInfo', payload);
      const slots = get(resp, 'data.availableSlots.0');

      const days = sortBy(keys(slots), (date: string) => {
        return moment(date, 'YYYY-MM-DD').valueOf();
      });

      setData(slots);
      setDayColumns(days);
      const today = moment().format('YYYY-MM-DD');

      setSelectedSlot(get(resp, 'data.reservedSlot'));
      const reservedSlotDay = get(resp, 'data.reservedSlot.slotDate');

      if (reservedSlotDay && !includes(days, reservedSlotDay)) {
        setSelectedDay(get(days, '0', ''));
      } else if (reservedSlotDay) {
        setSelectedDay(reservedSlotDay);
      } else {
        setSelectedDay(includes(days, today) ? today : get(days, '0', ''));
      }
    } catch (err) {
      console.error(err);
      setInfoError(true);
    } finally {
      setLoadingInfo(false);
    }
  };

  const saveSlots = async () => {
    setSavingSlots(true);
    const result = await invoke(currentPreferencesContext, 'updateSlotsInfo', {
      slotId: get(selectedSlot, 'slotId'),
      fulfillmentDay: get(selectedSlot, 'dayOfWeek'),
      slotStartTS: get(selectedSlot, 'displayStartTS'),
      slotEndTS: get(selectedSlot, 'displayEndTS'),
      slotType: get(selectedSlot, 'slotType'),
    });

    if (get(result, 'error', false)) {
      invoke(props, 'onError');
    } else {
      invoke(props, 'onConfirm');
      analyticsEvents(
        `ar:${window['digitalData']?.pageFlags?.isSnSItemsListExpanded ? '' : 'collapsed-state-'}change-date-time|save`,
        selectedSlot,
      );
    }

    setSavingSlots(false);
  };

  useEffect(() => {
    fetchAvailableSlotsInfo();
  }, []);

  useEffect(() => {
    if (!includes(slotTypes, get(selectedSlot, 'slotType'))) {
      setInvalidSelection(true);
    } else {
      setInvalidSelection(false);
    }
  }, [selectedSlot]);

  useEffect(() => {
    if (infoError) {
      invoke(currentModalContext, 'setModalError', AppLabels.AvailableSlotPreferences.LOADING_ERROR);
    }
  }, [infoError]);

  const selectedDaySlots = get(data, `${selectedDay}.slots`);
  const daySlotsByType = groupBy(selectedDaySlots, 'slotType');

  return (
    <div className={`item-list-container ${SlotPreferencesStyle}`}>
      <TimeSlotContext.Provider value={{ selectedSlot, setSelectedSlot }}>
        <div className={timeSlotHeadersRow}>
          {dayColumns.map((date: string) => (
            <SlotDayHeader key={date} day={date} active={selectedDay === date} onClick={() => setSelectedDay(date)} />
          ))}
        </div>
        <div className={`item-list-container ${SlotColumnsContainerStyle}`}>
          {loadingInfo ? (
            <Loader></Loader>
          ) : !data ? (
            <div className={errorContainer}>
              <UserMessage messageType="warning" messageText={AppLabels.AvailableSlotPreferences.LOADING_ERROR} />
            </div>
          ) : (
            <SlotTimesColumn columnData={selectedDaySlots} />
          )}
        </div>
      </TimeSlotContext.Provider>

      {!loadingInfo ? (
        <div className={buttonContainer}>
          <p>
            <span>{AppLabels.AvailableSlotPreferences.productAvailability}</span>
          </p>
          <button
            className={`${saveSlotsButtonStyle} ${savingSlots ? 'saving' : ''}`}
            onClick={saveSlots}
            disabled={savingSlots || invalidSelection}
            aria-label="Save slot preferences"
          >
            {savingSlots ? <Spinner /> : <span className="body-text">Save</span>}
          </button>
        </div>
      ) : null}
    </div>
  );
};

AvailableSlotPreferences.propTypes = {
  pickup: PropTypes.bool,
  onConfirm: PropTypes.func,
  onError: PropTypes.func,
  preselectedSlot: PropTypes.object,
  nextOrderData: PropTypes.object.isRequired,
};

export default AvailableSlotPreferences;
